import React, { useState } from "react";
import FormInput from "Common/Custom/FormInput";
import FormTextarea from "Common/Custom/FormTextarea";
import FormInputFile from "Common/Custom/FormInputFile";
import FormPhoneInput from "Common/Custom/FormPhoneInput";
import FormSubmitButton from "Common/Custom/FormSubmitButton";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import { getSelectOptions } from "utils/optionHelper";
import FormSelect from "Common/Custom/FormSelect";
import states from "Common/json/States.json";
import { validateField } from "utils/validation";
import FormDatePicker from "Common/Custom/ReactFlatpickr";
import PropTypes from "prop-types";

const AddRecruitmentForm = ({
  fields = [],
  values = {},
  handleChange = () => { },
  handleFileChange = () => { },
  setValues = () => { },
  handleSubmit = () => { },
  loading = false,
  title = "Add Recruitment",
  errors = {},
  setErrors = () => { },
  handleArrayChange = () => { },
  rules = {},
  buildInitialObject = () => { },
  imagePreview = "",
}) => {

  const [activeTab, setActiveTab] = useState("basic");

  const tabs = [
    { key: "basic", label: "Basic Info", icon: "bi-person-badge" },
    { key: "education", label: "Education Info", icon: "bi-book" },
    { key: "experience", label: "Experience Info", icon: "bi-briefcase" }
  ];

  const handleTabChange = (targetTab) => {
    const tabOrder = tabs.map(t => t.key);
    const currentIndex = tabOrder.indexOf(activeTab);
    const targetIndex = tabOrder.indexOf(targetTab);

    if (targetIndex <= currentIndex) {
      setActiveTab(targetTab);
      return;
    }

    if (currentIndex === 0 && targetIndex >= 1) {
      const basicRules = rules
        ? Object.fromEntries(
          Object.entries(rules).filter(([, v]) => Array.isArray(v))
        )
        : {};

      const basicErrors = {};
      Object.keys(basicRules).forEach((fieldName) => {
        const error = validateField(fieldName, values[fieldName], basicRules);
        if (error) basicErrors[fieldName] = error;
      });

      if (Object.keys(basicErrors).length) {
        setErrors((prev) => ({ ...prev, ...basicErrors }));
        return;
      }
    }

    if (currentIndex === 1 && targetIndex >= 2) {
      const educationRules = rules?.education || {};
      let hasError = false;
      const educationErrors = (values.education || []).map((row) => {
        const rowErrors = {};
        Object.keys(educationRules).forEach((field) => {
          const error = validateField(field, row[field], educationRules);
          if (error) { rowErrors[field] = error; hasError = true; }
        });
        return rowErrors;
      });

      if (hasError) {
        setErrors((prev) => ({ ...prev, education: educationErrors }));
        return;
      }
    }

    setActiveTab(targetTab);
  };

  const optionConfig = {
    state: {
      list: states?.states,
      valueKey: "value",
      labelKey: "label"
    },
  };

  const renderField = (field, value, onChange, error, extraProps = {}) => {
    if (field.type === "file")
      return <FormInputFile key={field.name} field={field} handleChange={handleFileChange} imagePreview={imagePreview} error={error} />;

    if (field.type === "textarea")
      return <FormTextarea key={field.name} field={field} value={value} handleChange={onChange} error={error} />;

    if (field.type === "phone") {
      const defaultCodeField =
        field.name === "phone"
          ? "phone_country_dial_code"
          : field.name === "supervisor_phone"
            ? "supervisor_phone_country_dial_code"
            : "alternate_phone_country_dial_code";

      const defaultCountryCode = values[defaultCodeField];

      return (
        <FormPhoneInput
          key={field.name}
          field={field}
          value={value}
          country_code={extraProps.country_code ?? defaultCountryCode}
          handleChange={extraProps.handleChange ?? handleChange}
          country_code_field={extraProps.country_code_field ?? defaultCodeField}
          error={error}
        />
      );
    }

    if (field.type === "select")
      return (
        <FormSelect
          key={field.name}
          field={field}
          value={value}
          handleChange={handleChange}
          options={getSelectOptions(field.name, optionConfig)}
          error={error}
        />
      );

    if (field.type === "date") {
      return (
        <FormDatePicker
          key={field.name}
          field={field}
          value={value}
          handleChange={onChange}
          error={error}
        />
      )
    }

    return (
      <FormInput
        key={field.name}
        field={field}
        value={value}
        handleChange={onChange}
        error={error}
      />
    );
  };

  const renderRepeatable = (section, sectionFields, icon) => (
    <div className={`${section}-container`}>
      <div className="section-header">
        <i className={`bi ${icon}`}></i>
        <h5 className="mb-0">{section.charAt(0).toUpperCase() + section.slice(1)} Details</h5>
      </div>
      {(values[section] || []).map((item, i) => (
        <div key={i} className="card repeatable-row position-relative">
          {values[section].length > 1 && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger position-absolute top-0 end-0 m-3"
              onClick={() => {
                const data = values[section].filter((_, index) => index !== i);
                setValues({ ...values, [section]: data });
              }}
            >
              <i className="bi bi-trash"></i>
            </button>
          )}

          <div className="row g-3">
            {sectionFields.map((field) => {
              const codeFieldName = field.name === "supervisor_phone"
                ? "supervisor_phone_country_dial_code"
                : field.name === "phone"
                  ? "phone_country_dial_code"
                  : field.name === "alternate_phone"
                    ? "alternate_phone_country_dial_code"
                    : null;

              const extraProps = field.type === "phone" && codeFieldName
                ? {
                  country_code: item[codeFieldName] || "+91",
                  country_code_field: codeFieldName,
                  handleChange: (e) =>
                    handleArrayChange(section, i, e.target.name, e.target.value),
                }
                : {};

              return renderField(
                field,
                item[field.name],
                (e) => handleArrayChange(section, i, field.name, e.target.value),
                errors?.[section]?.[i]?.[field.name],
                extraProps
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <PageBody onSubmit={handleSubmit} headerchildren={<HeadingOne>{title}</HeadingOne>}>
      <div className="recruitment-tabs-container">
        <ul className="nav nav-tabs recruitment-tabs">
          {tabs.map((t) => {
            const hasBasicError = t.key === "basic" && errors && Object.keys(errors).some((k) => !["education", "experience"].includes(k) && errors[k]);
            const hasEduError = t.key === "education" && errors?.education && errors.education.some((r) => Object.keys(r || {}).length);
            const hasExpError = t.key === "experience" && errors?.experience && errors.experience.some((r) => Object.keys(r || {}).length);
            const tabHasError = hasBasicError || hasEduError || hasExpError;

            return (
              <li className="nav-item" key={t.key}>
                <button
                  type="button"
                  className={`nav-link ${activeTab === t.key ? "active" : ""}`}
                  onClick={() => handleTabChange(t.key)}
                >
                  <i className={t.icon}></i>
                  <span>{t.label}</span>
                  {tabHasError && (
                    <span className="badge bg-danger error-badge ms-1">!</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="tab-content">
        <div className="form-section-content">
          {activeTab === "basic" && (
            <div className="basic-info-container">
              <div className="section-header">
                <i className="bi bi-person-badge-fill"></i>
                <h5 className="mb-0">Candidate Basic Information</h5>
              </div>
              <div className="row g-3">
                {(fields.basic || []).map((field) => (
                  renderField(field, values[field.name], handleChange, errors[field.name])
                ))}
              </div>
            </div>
          )}

          {activeTab === "education" && (
            <>
              {renderRepeatable("education", fields.education, "bi-book-half")}
              <button
                type="button"
                className="btn btn-outline-primary btn-add-more"
                onClick={() =>
                  setValues({
                    ...values,
                    education: [
                      ...values.education,
                      buildInitialObject(fields.education)
                    ]
                  })
                }
              >
                <i className="bi bi-plus-circle"></i> Add Education
              </button>
            </>
          )}

          {activeTab === "experience" && (
            <>
              {renderRepeatable("experience", fields.experience, "bi-briefcase")}
              <button
                type="button"
                className="btn btn-outline-primary btn-add-more"
                onClick={() =>
                  setValues({
                    ...values,
                    experience: [
                      ...values.experience,
                      { ...buildInitialObject(fields.experience), supervisor_phone_country_dial_code: "+91" }
                    ]
                  })
                }
              >
                <i className="bi bi-plus-circle"></i> Add Experience
              </button>
            </>
          )}
        </div>
      </div>

      <div className="form-navigation d-flex justify-content-end gap-3 mt-4">
        <button
          type="button"
          className="btn btn-prev"
          onClick={() => {
            const tabOrder = tabs.map(t => t.key);
            const prevTab = tabOrder[tabOrder.indexOf(activeTab) - 1];
            if (prevTab) handleTabChange(prevTab);
          }}
          disabled={activeTab === tabs[0].key}
        >
          Previous
        </button>

        {activeTab !== tabs[tabs.length - 1].key ? (
          <button
            type="button"
            className="btn btn-next"
            onClick={() => {
              const tabOrder = tabs.map(t => t.key);
              const nextTab = tabOrder[tabOrder.indexOf(activeTab) + 1];
              if (nextTab) handleTabChange(nextTab);
            }}
          >
            Next
          </button>
        ) : (
          <FormSubmitButton
            label="Save Candidate"
            loading={loading}
            colSize="auto"
            className="btn-submit"
          />
        )}
      </div>
    </PageBody>
  );
};


AddRecruitmentForm.propTypes = {
  fields: PropTypes.array,
  values: PropTypes.object,
  handleChange: PropTypes.func,
  handleFileChange: PropTypes.func,
  setValues: PropTypes.func,
  handleSubmit: PropTypes.func,
  loading: PropTypes.bool,
  title: PropTypes.string,
  errors: PropTypes.object,
  setErrors: PropTypes.func,
  handleArrayChange: PropTypes.func,
  rules: PropTypes.object,
  buildInitialObject: PropTypes.func,
  imagePreview: PropTypes.object,
};

AddRecruitmentForm.defaultProps = {
  fields: [],
  values: {},
  handleChange: () => { },
  handleFileChange: () => { },
  setValues: () => { },
  handleSubmit: () => { },
  loading: false,
  title: "Add Recruitment",
  errors: {},
  setErrors: () => { },
  handleArrayChange: () => { },
  rules: {},
  buildInitialObject: () => { }
};

export default AddRecruitmentForm;
